/**
 * CardHeroMenu — Three-dot menu for editing the hero image on a ProjectCard.
 *
 * Controls: Upload / Change Photo, Edit Crop, Paste URL, Remove Photo.
 * Reuses the same CropOverlay pattern from ProjectDetailPanel but sized for
 * the card's aspect ratio (≈ square).
 *
 * Renders as a portal so the crop overlay can cover the full viewport.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { uploadDataUrl, fileUrl } from '../../api/fileStorage';
import { setProjectOverride } from '../../Data/ProjectConfig/projectConfigOverrides';
import { useAppTranslation } from '../../i18n/useAppTranslation';

/* ── Constants ─────────────────────────────────────────────────── */

const MAX_RAW_FILE_BYTES = 10 * 1024 * 1024;
const JPEG_QUALITY = 0.92;
const CROP_OUTPUT_W = 1200; // cards are smaller than hero banners

/* ── CropState ─────────────────────────────────────────────────── */

interface CropState {
  src: string;
  naturalW: number;
  naturalH: number;
}

/* ── CropOverlay (card variant — 1 : 1 square target) ─────────── */

function CropOverlay({
  crop,
  onConfirm,
  onCancel,
  targetAspect = 1,
}: {
  crop: CropState;
  onConfirm: (dataUrl: string) => void;
  onCancel: () => void;
  targetAspect?: number;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportW, setViewportW] = useState(400);
  const [viewportH, setViewportH] = useState(400);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{
    startX: number; startY: number;
    startPanX: number; startPanY: number;
  } | null>(null);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        setViewportW(e.contentRect.width);
        setViewportH(e.contentRect.height);
      }
    });
    ro.observe(el);
    setViewportW(el.clientWidth);
    setViewportH(el.clientHeight || 400);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const { naturalW, naturalH } = crop;
  const baseScale = Math.max(viewportW / naturalW, viewportH / naturalH);
  const displayScale = baseScale * zoom;
  const displayW = naturalW * displayScale;
  const displayH = naturalH * displayScale;

  const clampPan = useCallback(
    (px: number, py: number, z?: number) => {
      const ds = baseScale * (z ?? zoom);
      const dw = naturalW * ds;
      const dh = naturalH * ds;
      const maxPanX = Math.max(0, (dw - viewportW) / 2);
      const maxPanY = Math.max(0, (dh - viewportH) / 2);
      return {
        x: Math.max(-maxPanX, Math.min(maxPanX, px)),
        y: Math.max(-maxPanY, Math.min(maxPanY, py)),
      };
    },
    [baseScale, naturalW, naturalH, viewportW, viewportH, zoom],
  );

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPanX: pan.x, startPanY: pan.y };
  }, [pan]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPan(clampPan(dragRef.current.startPanX + dx, dragRef.current.startPanY + dy));
  }, [clampPan]);

  const onPointerUp = useCallback(() => { dragRef.current = null; }, []);

  const onWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const next = Math.max(1, Math.min(4, zoom - e.deltaY * 0.002));
    setZoom(next);
    setPan((p) => clampPan(p.x, p.y, next));
  }, [zoom, clampPan]);

  const handleZoomSlider = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const next = parseFloat(e.target.value);
    setZoom(next);
    setPan((p) => clampPan(p.x, p.y, next));
  }, [clampPan]);

  const handleConfirm = useCallback(() => {
    const aspect = targetAspect > 0 ? targetAspect : viewportW / viewportH;
    const visSrcW = viewportW / displayScale;
    const visSrcH = viewportH / displayScale;
    const visSrcCX = (displayW / 2 - pan.x) / displayScale;
    const visSrcCY = (displayH / 2 - pan.y) / displayScale;

    let srcW: number, srcH: number;
    if (visSrcW / visSrcH > aspect) {
      srcW = visSrcW; srcH = visSrcW / aspect;
    } else {
      srcH = visSrcH; srcW = visSrcH * aspect;
    }

    // DEV-1260 crop fix: never sample outside the source image. When the
    // computed window exceeds the image bounds, shrink it (preserving
    // aspect) instead of letting drawImage read past the edge — that's
    // what produced the black bars. With the viewport aspect now matched
    // to the output aspect this is a no-op safety net.
    const fit = Math.min(naturalW / srcW, naturalH / srcH, 1);
    srcW *= fit;
    srcH *= fit;

    const srcX = Math.max(0, Math.min(naturalW - srcW, visSrcCX - srcW / 2));
    const srcY = Math.max(0, Math.min(naturalH - srcH, visSrcCY - srcH / 2));

    const outW = CROP_OUTPUT_W;
    const outH = Math.round(CROP_OUTPUT_W / aspect);
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, outW, outH);
      onConfirm(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
    };
    img.src = crop.src;
  }, [crop.src, naturalW, naturalH, displayW, displayH, displayScale, pan, viewportW, viewportH, targetAspect, onConfirm]);

  const imgStyle: React.CSSProperties = {
    position: 'absolute',
    width: displayW,
    height: displayH,
    left: (viewportW - displayW) / 2 + pan.x,
    top: (viewportH - displayH) / 2 + pan.y,
    userSelect: 'none',
    pointerEvents: 'none',
  };

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        width: '90vw', maxWidth: 520,
        background: 'var(--bg-elevated)',
        borderRadius: 12, overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(0,0,0,0.7)',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          padding: '8px 12px', fontSize: 12, fontWeight: 600,
          color: 'var(--text-secondary)', background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>Drag to reposition &middot; scroll to zoom</span>
          <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{Math.round(zoom * 100)}%</span>
        </div>
        <div
          ref={viewportRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onWheel={onWheel}
          style={{
            // DEV-1260 crop fix: the preview viewport MUST match the
            // output aspect, or the confirm math samples outside the
            // framed area (black bars on wide sources). WYSIWYG: what
            // you frame is exactly what is exported.
            width: '100%',
            aspectRatio: `${targetAspect}`,
            maxHeight: '60vh',
            position: 'relative', overflow: 'hidden',
            cursor: dragRef.current ? 'grabbing' : 'grab',
            touchAction: 'none',
          }}
        >
          <img src={crop.src} alt="Crop preview" draggable={false} style={imgStyle} />
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}>
            {[1, 2].map((i) => (
              <React.Fragment key={i}>
                <div style={{ position: 'absolute', left: `${(i / 3) * 100}%`, top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.15)' }} />
                <div style={{ position: 'absolute', top: `${(i / 3) * 100}%`, left: 0, right: 0, height: 1, background: 'rgba(255,255,255,0.15)' }} />
              </React.Fragment>
            ))}
          </div>
        </div>
        <div style={{
          padding: '8px 12px', background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 10, color: 'var(--text-tertiary)', flexShrink: 0 }}>Zoom</span>
          <input
            type="range" min="1" max="4" step="0.01"
            value={zoom} onChange={handleZoomSlider}
            style={{ flex: 1, accentColor: 'var(--accent-secondary)', cursor: 'pointer' }}
          />
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" onClick={onCancel} style={{
              background: 'var(--bg-hover)', border: '1px solid var(--border-default)',
              borderRadius: 5, color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600,
              padding: '5px 12px', cursor: 'pointer',
            }}>Cancel</button>
            <button type="button" onClick={handleConfirm} style={{
              background: 'rgba(69,187,71,0.75)', border: '1px solid var(--border-default)',
              borderRadius: 5, color: 'var(--text-primary)', fontSize: 12, fontWeight: 600,
              padding: '5px 12px', cursor: 'pointer',
            }}>Save Crop</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ── Menu item style ───────────────────────────────────────────── */

const menuItemStyle: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'start',
  background: 'transparent', border: 'none',
  borderBottom: '1px solid var(--border-subtle)',
  color: 'var(--text-primary)', fontSize: 12, fontWeight: 500,
  padding: '9px 14px', cursor: 'pointer',
  transition: 'background 0.12s',
};

/* ── CardHeroMenu ──────────────────────────────────────────────── */

export interface CardHeroMenuProps {
  projectGuid: string;
  currentHeroUrl: string | null;
  /** Callback after the hero image changes so the parent can refresh. */
  onHeroChanged?: () => void;
  /**
   * DEV-1260. Optional persistence override. When provided it replaces
   * the default setProjectOverride path — used by Mini-Project cards
   * to PATCH the entity's cardImageUrl via the API.
   * Receives the new URL; '' means remove.
   */
  persistOverride?: (url: string) => void;
  /**
   * Skip 2026-05-28. MinIO bucket for the upload. Defaults to 'heroes'
   * (FP). Mini-Project cards pass 'mini-project-hero' so uploads land
   * in the entity-appropriate bucket and don't fail at the storage
   * layer. The function inside this component is identical for both
   * tiers — only the bucket name is parametrized, matching how the
   * detail-page hero edit already chooses its bucket per entity type.
   */
  uploadBucket?: string;
}

const CardHeroMenu: React.FC<CardHeroMenuProps> = ({
  projectGuid,
  currentHeroUrl,
  onHeroChanged,
  persistOverride,
  uploadBucket = 'heroes',
}) => {
  const { t } = useAppTranslation('pages');
  const [menuOpen, setMenuOpen] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const [cropState, setCropState] = useState<CropState | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* Close menu on outside click */
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  /* ── Persist helper ── */
  const persistHeroUrl = useCallback(
    (url: string) => {
      if (persistOverride) {
        persistOverride(url); // DEV-1260: entity-specific persistence
      } else {
        setProjectOverride(projectGuid, { card_image_url: url });
      }
      onHeroChanged?.();
    },
    [projectGuid, onHeroChanged, persistOverride],
  );

  /* ── File upload ── */
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert(t('projectCard.heroSelectImage', 'Please select an image file.')); return; }
    if (file.size > MAX_RAW_FILE_BYTES) { alert(t('projectCard.heroImageTooLarge', 'Image is too large. Maximum is 10 MB.')); return; }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { setCropState({ src: objectUrl, naturalW: img.naturalWidth, naturalH: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); alert(t('projectCard.heroLoadFailed', 'Failed to load image.')); };
    img.src = objectUrl;
    e.target.value = '';
  }, [t]);

  /* ── Crop confirm ── */
  const handleCropConfirm = useCallback((dataUrl: string) => {
    if (cropState) URL.revokeObjectURL(cropState.src);
    setCropState(null);
    const ts = Date.now();
    uploadDataUrl(dataUrl, uploadBucket, projectGuid, `hero-${ts}.jpg`)
      .then((result) => {
        persistHeroUrl(`${fileUrl(result.key)}?v=${ts}`);
      })
      .catch(() => {
        persistHeroUrl(dataUrl);
      });
  }, [cropState, persistHeroUrl, projectGuid]);

  const handleCropCancel = useCallback(() => {
    if (cropState) URL.revokeObjectURL(cropState.src);
    setCropState(null);
  }, [cropState]);

  /* ── Edit existing hero (re-crop) ── */
  const handleEditHero = useCallback(() => {
    if (!currentHeroUrl) return;
    fetch(currentHeroUrl)
      .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.blob(); })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => { setCropState({ src: blobUrl, naturalW: img.naturalWidth, naturalH: img.naturalHeight }); };
        img.onerror = () => { URL.revokeObjectURL(blobUrl); alert(t('projectCard.heroCropFailed', 'Could not load the current hero image for editing.')); };
        img.src = blobUrl;
      })
      .catch(() => { alert(t('projectCard.heroCropFailed', 'Could not load the current hero image for editing.')); });
  }, [currentHeroUrl, t]);

  /* ── Paste URL ── */
  const handleUrlSubmit = useCallback(() => {
    const url = urlDraft.trim();
    if (!url) return;
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('data:')) {
      alert(t('projectCard.heroInvalidUrl', 'Please enter a valid URL starting with http:// or https://'));
      return;
    }
    if (url.startsWith('data:') && projectGuid) {
      uploadDataUrl(url, uploadBucket, projectGuid, 'hero.jpg')
        .then((result) => { persistHeroUrl(fileUrl(result.key)); })
        .catch(() => { persistHeroUrl(url); });
    } else {
      persistHeroUrl(url);
    }
    setShowUrlInput(false);
    setUrlDraft('');
  }, [urlDraft, persistHeroUrl, projectGuid, t]);

  /* ── Remove ── */
  const handleRemove = useCallback(() => {
    if (persistOverride) {
      persistOverride(''); // DEV-1260: entity-specific removal
    } else {
      setProjectOverride(projectGuid, { card_image_url: '' });
    }
    onHeroChanged?.();
  }, [projectGuid, onHeroChanged, persistOverride]);

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Three-dot trigger */}
      <div
        ref={menuRef}
        style={{ position: 'absolute', top: 8, insetInlineEnd: 8, zIndex: 6 }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
          aria-label={t('projectCard.heroMenuLabel', 'Card image options')}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28,
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 7, cursor: 'pointer',
            color: 'rgba(255,255,255,0.85)',
            opacity: 0.7, transition: 'opacity 0.15s',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.7'; }}
        >
          {/* Vertical three dots */}
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="8" cy="3" r="1.5" />
            <circle cx="8" cy="8" r="1.5" />
            <circle cx="8" cy="13" r="1.5" />
          </svg>
        </button>

        {/* Dropdown menu */}
        {menuOpen && (
          <div style={{
            position: 'absolute', top: '100%', insetInlineEnd: 0,
            marginTop: 4,
            background: 'var(--bg-elevated)', backdropFilter: 'blur(8px)',
            borderRadius: 10, overflow: 'hidden',
            border: '1px solid var(--border-default)',
            boxShadow: '0 8px 28px rgba(0,0,0,0.5)', minWidth: 170,
            zIndex: 60,
          }}>
            <button
              type="button"
              onClick={() => { fileInputRef.current?.click(); setMenuOpen(false); }}
              style={menuItemStyle}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              {currentHeroUrl
                ? t('projectCard.heroChange', 'Change Photo')
                : t('projectCard.heroUpload', 'Upload Photo')}
            </button>
            {currentHeroUrl && (
              <button
                type="button"
                onClick={() => { handleEditHero(); setMenuOpen(false); }}
                style={menuItemStyle}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                {t('projectCard.heroEditCrop', 'Edit Crop')}
              </button>
            )}
            <button
              type="button"
              onClick={() => { setShowUrlInput(true); setUrlDraft(''); setMenuOpen(false); }}
              style={menuItemStyle}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              {t('projectCard.heroPasteUrl', 'Paste URL')}
            </button>
            {currentHeroUrl && (
              <button
                type="button"
                onClick={() => { handleRemove(); setMenuOpen(false); }}
                style={{ ...menuItemStyle, color: '#f87171', borderBottom: 'none' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-hover)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                {t('projectCard.heroRemove', 'Remove Photo')}
              </button>
            )}
          </div>
        )}

        {/* URL input popover */}
        {showUrlInput && !menuOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', top: '100%', insetInlineEnd: 0,
              marginTop: 4, display: 'flex', gap: 4, maxWidth: 320,
            }}
          >
            <input
              type="text"
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') handleUrlSubmit();
                if (e.key === 'Escape') { setShowUrlInput(false); setUrlDraft(''); }
              }}
              placeholder="https://example.com/image.jpg"
              autoFocus
              style={{
                flex: 1, background: 'rgba(0,0,0,0.80)',
                border: '1px solid var(--border-subtle)', borderRadius: 5,
                color: 'var(--text-secondary)', fontSize: 12, padding: '5px 8px',
                outline: 'none', minWidth: 160,
              }}
            />
            <button type="button" onClick={handleUrlSubmit} style={{
              background: 'rgba(69,187,71,0.70)', border: '1px solid var(--border-default)',
              borderRadius: 5, color: 'var(--text-primary)', fontSize: 12, fontWeight: 600,
              padding: '5px 10px', cursor: 'pointer',
            }}>Save</button>
          </div>
        )}
      </div>

      {/* Crop overlay — rendered as a portal over entire viewport */}
      {cropState && (
        <CropOverlay
          crop={cropState}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
          targetAspect={1}
        />
      )}
    </>
  );
};

export default CardHeroMenu;
